import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';

import TimestampNodeView from './TimestampNodeView';

export interface TimestampOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    timestamp: {
      /**
       * Set timestamp value and visibility.
       * @param value - Timestamp string (e.g., "00:13") or null to hide
       */
      setTimestamp: (value: string | null) => ReturnType;
    };
  }
}

/**
 * TimestampNode - A non-editable ProseMirror atom node for displaying timestamps.
 *
 * Key features:
 * - Atom node: cannot be entered by cursor
 * - Not selectable: excluded from selection ranges
 * - Always positioned as first document node
 * - Controlled via transactions (no user interaction)
 * - Does not affect text content or mention ranges
 */
export const TimestampNode = Node.create<TimestampOptions>({
  name: 'timestamp',

  group: 'inline',

  inline: true,

  atom: true, // Prevents cursor from entering the node

  selectable: false, // Excludes from selection ranges

  draggable: false,

  // Mark as not content so placeholder still shows
  defining: false,

  // Prevent deletion via backspace/delete
  marks: '',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-value'),
        renderHTML: (attributes) => {
          return attributes.value ? { 'data-value': attributes.value } : {};
        },
      },
      hidden: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-hidden') === 'true',
        renderHTML: (attributes) => {
          return { 'data-hidden': String(attributes.hidden) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-timestamp]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-timestamp': '',
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TimestampNodeView);
  },

  addCommands() {
    return {
      setTimestamp:
        (value: string | null) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;
          const hidden = !value;
          const normalizedValue = value || '';

          // Mark this transaction so filterTransaction allows it
          tr.setMeta('fromSetTimestamp', true);

          // Find ALL timestamp nodes in the document
          const allTimestamps: Array<{ node: any; pos: number; isFirst: boolean }> = [];
          let firstTimestampFound = false;

          doc.descendants((node, pos) => {
            if (node.type.name === 'timestamp') {
              const isFirst = !firstTimestampFound;
              if (isFirst) {
                firstTimestampFound = true;
              }
              allTimestamps.push({ node, pos, isFirst });
            }
          });

          // Keep only the FIRST timestamp, remove all others
          const timestampsToRemove = allTimestamps.filter((t) => !t.isFirst);

          // Remove in reverse order to maintain correct positions
          for (let i = timestampsToRemove.length - 1; i >= 0; i--) {
            const { pos, node } = timestampsToRemove[i];
            tr.delete(pos, pos + node.nodeSize);
          }

          // Find the first (and now only) timestamp
          const firstTimestamp = allTimestamps.find((t) => t.isFirst);

          // Case 1: First timestamp exists - update it
          if (firstTimestamp) {
            const { node, pos } = firstTimestamp;
            const currentValue = node.attrs.value;
            const currentHidden = node.attrs.hidden;

            // Only update if attributes actually changed
            if (currentValue !== normalizedValue || currentHidden !== hidden) {
              tr.setNodeMarkup(pos, undefined, {
                value: normalizedValue,
                hidden,
              });
            }
          } else {
            // Case 2: No timestamp - insert at start of first paragraph
            const firstChild = doc.firstChild;
            if (firstChild && firstChild.type.name === 'paragraph') {
              const newTimestampNode = state.schema.nodes.timestamp.create({
                value: normalizedValue,
                hidden,
              });

              // Insert at position 1 (inside first paragraph, at the start)
              tr.insert(1, newTimestampNode);
            }
          }

          if (dispatch && tr.docChanged) {
            dispatch(tr);
          }

          return true;
        },
    };
  },

  // Add ProseMirror plugins for timestamp management
  addProseMirrorPlugins() {
    return [
      // Plugin 1: Prevent cursor from moving before timestamp
      new Plugin({
        key: new PluginKey('timestampCursorBlock'),
        appendTransaction: (transactions, oldState, newState) => {
          // Skip if no selection change
          if (oldState.selection.eq(newState.selection)) {
            return null;
          }

          const { selection, doc } = newState;
          const { $from } = selection;

          // Only block collapsed cursor (not text selections)
          if (!selection.empty) {
            return null;
          }

          // Check if cursor is at the start of the first paragraph
          if ($from.pos === 1) {
            // Check if there's a timestamp at position 1
            const firstChild = doc.firstChild;
            if (firstChild && firstChild.type.name === 'paragraph') {
              const firstNode = firstChild.firstChild;
              if (firstNode && firstNode.type.name === 'timestamp') {
                // Move cursor after the timestamp
                const tr = newState.tr;
                const timestampEndPos = 1 + firstNode.nodeSize;
                tr.setSelection(TextSelection.near(doc.resolve(timestampEndPos), 1));
                return tr;
              }
            }
          }

          return null;
        },
      }),
      // Plugin 2: Clean up duplicate timestamps
      new Plugin({
        key: new PluginKey('timestampCleanup'),
        appendTransaction: (transactions, oldState, newState) => {
          // Skip if this is from setTimestamp command (it already cleans up)
          if (transactions.some((tr) => tr.getMeta('fromSetTimestamp'))) {
            return null;
          }

          // Skip if this is from our own cleanup
          if (transactions.some((tr) => tr.getMeta('fromTimestampCleanup'))) {
            return null;
          }

          // Check if first timestamp was deleted, if so restore it
          let oldFirstTimestamp: any = null;
          let oldFirstTimestampFound = false;
          oldState.doc.descendants((node) => {
            if (!oldFirstTimestampFound && node.type.name === 'timestamp') {
              oldFirstTimestamp = node;
              oldFirstTimestampFound = true;
              return false;
            }
          });

          let newFirstTimestampExists = false;
          newState.doc.descendants((node) => {
            if (node.type.name === 'timestamp') {
              newFirstTimestampExists = true;
              return false;
            }
          });

          // If we had a timestamp and now we don't, restore it
          if (oldFirstTimestamp && !newFirstTimestampExists) {
            const tr = newState.tr;
            tr.setMeta('fromTimestampCleanup', true);

            const firstChild = newState.doc.firstChild;
            if (firstChild && firstChild.type.name === 'paragraph') {
              tr.insert(1, oldFirstTimestamp);
              return tr;
            }
          }

          // Count timestamps in the document
          const timestamps: Array<{ pos: number; node: any }> = [];
          newState.doc.descendants((node, pos) => {
            if (node.type.name === 'timestamp') {
              timestamps.push({ pos, node });
            }
          });

          // If more than 1 timestamp exists, remove all except the first
          if (timestamps.length > 1) {
            const tr = newState.tr;
            tr.setMeta('fromTimestampCleanup', true);

            // Remove all timestamps except the first, in reverse order
            for (let i = timestamps.length - 1; i > 0; i--) {
              const { pos, node } = timestamps[i];
              tr.delete(pos, pos + node.nodeSize);
            }

            return tr;
          }

          return null;
        },
      }),
    ];
  },
});
